/**
 * Bulk recovery from a parser-version bump.
 *
 * Every bump strands every document already parsed: its run is at the old version, so
 * nothing at the current one resolves until it is read again. One document at a time is
 * fine for one document; it is not fine for a corpus, and this is the third bump in a
 * day. So the list offers to re-parse the lot.
 *
 * Requests go out a few at a time rather than all at once — the queue is the thing doing
 * the work, and there is no reason to hand it a thundering herd to serialise anyway.
 */
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { post } from "../lib/api";
import type { DocumentSummary } from "../lib/types";
import { Button } from "./ui";

const AT_A_TIME = 4;

export function StaleBanner({
  documents,
  parserVersion,
}: {
  documents: DocumentSummary[];
  parserVersion: string;
}) {
  // Documents read by an older build. They are readable — this offers a refresh, it does
  // not unblock anything. Genuinely broken documents (status "stale") are handled on the
  // detail screen, because they need looking at rather than batching.
  const stale = documents.filter((doc) => doc.current === false);
  const [sent, setSent] = useState(0);
  const [running, setRunning] = useState(false);
  const [failed, setFailed] = useState<string[]>([]);
  const queryClient = useQueryClient();

  if (stale.length === 0) return null;

  async function reparseAll() {
    setRunning(true);
    setSent(0);
    setFailed([]);
    const queue = [...stale];

    async function worker() {
      for (;;) {
        const doc = queue.shift();
        if (!doc) return;
        try {
          await post(`/v1/documents/${doc.id}/reparse`, {});
        } catch {
          setFailed((current) => [...current, doc.filename ?? doc.id.slice(0, 8)]);
        }
        setSent((count) => count + 1);
      }
    }

    await Promise.all(Array.from({ length: AT_A_TIME }, worker));
    setRunning(false);
    void queryClient.invalidateQueries({ queryKey: ["documents"] });
    void queryClient.invalidateQueries({ queryKey: ["jobs"] });
  }

  return (
    <div className="flex shrink-0 flex-wrap items-center gap-x-4 gap-y-2 border border-warn/40 bg-warn-wash px-4 py-2.5">
      <span className="font-mono text-[10.5px] font-bold tracking-[0.16em] text-warn uppercase">
        {stale.length} read by an older build
      </span>
      <span className="min-w-0 text-[12.5px] text-dim">
        Their pages and findings cannot be shown until they are read again at{" "}
        <span className="font-mono text-warn">{parserVersion}</span>.
      </span>
      <span className="ms-auto flex items-center gap-3">
        {running && (
          <span className="tnum font-mono text-[11px] text-dim">
            {sent} / {stale.length}
          </span>
        )}
        {failed.length > 0 && !running && (
          <span className="font-mono text-[11px] text-crit">{failed.length} could not queue</span>
        )}
        <Button variant="primary" disabled={running} onClick={() => void reparseAll()}>
          {running ? "queueing…" : `re-read all ${stale.length}`}
        </Button>
      </span>
    </div>
  );
}
